import React, { Component } from 'react';
import {
  array,
  bool,
  func,
  oneOf,
  object,
  shape,
  string,
  arrayOf,
} from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

// Seeded random function for consistent randomization during session
const seededRandom = seed => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Fisher-Yates shuffle algorithm with seeded random
const shuffleArray = (array, seed) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
} from '../../ducks/ui.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { ConditionToLabel } from '../../util/enums/CONDITION';

import { H3, H5, NamedRedirect, Page } from '../../components';
import TopbarSpacer from '../TopbarContainer/TopbarSpacer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SaveSearchButton from './SaveSearchButton/SaveSearchButton';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 200000; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    // Generate a stable hourly seed for randomization
    const currentHour = Math.floor(Date.now() / (1000 * 60 * 60));
    this.hourlySeed = currentHour;

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Saved searches
    this.handleToggleSavedSearch = this.handleToggleSavedSearch.bind(this);

    this._scrollKey = `searchScroll_${props.location.search}`;
    // The constructor also runs during SSR, where sessionStorage doesn't exist
    this._scrollRestorePending =
      typeof window !== 'undefined' && !!window.sessionStorage.getItem(this._scrollKey);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchInProgress && !this.props.searchInProgress && this._scrollRestorePending) {
      const saved = sessionStorage.getItem(this._scrollKey);
      if (saved) {
        window.scrollTo(0, parseInt(saved, 10));
        sessionStorage.removeItem(this._scrollKey);
      }
      this._scrollRestorePending = false;
    }
  }

  componentWillUnmount() {
    sessionStorage.setItem(this._scrollKey, window.scrollY);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(
      listingFieldsConfig,
      defaultFiltersConfig
    );

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params and remove keywords explicitly
    const queryParams = omit(urlQueryParams, [
      ...filterQueryParamNames,
      'keywords',
    ]);
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        queryParams
      )
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } =
      config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = {
          ...urlQueryParams,
          ...prevState.currentQueryParams,
        };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config)
          ? { keywords }
          : {};
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            filterConfigs,
            sortConfig
          );
          history.push(
            createResourceLocatorString(
              'SearchPage',
              routeConfiguration,
              {},
              search
            )
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        queryParams
      )
    );
  }

  // Add or remove the current keyword search from the user's saved searches.
  // Reuses onUpdateProfile (same path as favorites) so currentUser + the saved
  // searches count stay in sync immediately.
  handleToggleSavedSearch() {
    const {
      currentUser,
      onUpdateProfile,
      history,
      routeConfiguration,
      location,
    } = this.props;

    const phrase = (parse(location.search).keywords || '').trim();
    if (!phrase) {
      return;
    }

    // Require login to save a search.
    if (!currentUser) {
      history.push(
        createResourceLocatorString('LoginPage', routeConfiguration, {}, {}),
        { from: `${location.pathname}${location.search}` }
      );
      return;
    }

    const privateData = currentUser.attributes?.profile?.privateData || {};
    const savedSearches = Array.isArray(privateData.savedSearches)
      ? privateData.savedSearches.filter(s => s && s.id)
      : [];
    const existing = savedSearches.find(
      s => (s.searchPhrase || '').trim().toLowerCase() === phrase.toLowerCase()
    );

    const updatedSavedSearches = existing
      ? savedSearches.filter(s => String(s.id) !== String(existing.id))
      : [
          ...savedSearches,
          {
            id: Date.now().toString(),
            searchPhrase: phrase,
            enableNotifications: false,
            createdAt: new Date().toISOString(),
          },
        ];

    onUpdateProfile({
      privateData: { ...privateData, savedSearches: updatedSavedSearches },
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      currentUser,
      onUpdateProfile,
      updateInProgress,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } =
      config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(
      config => config.listingType
    );
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Define the valid filter parameters that you want to include
    const VALID_FILTER_PARAMS = [
      'keywords',
      'pub_categoryLevel1',
      'pub_condition',
      // Add any other valid filter parameters here
    ];

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const {
      searchParamsAreInSync,
      urlQueryParams,
      searchParamsInURL,
    } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = urlQueryParams;

    // Handle 'popular' sort by randomizing listings
    const currentSort = validQueryParams[sortConfig.queryParamName];
    // If no sort is specified (null/undefined), default to 'popular'
    const effectiveSort = currentSort || 'popular';
    const processedListings =
      effectiveSort === 'popular' && listings && listings.length > 0
        ? shuffleArray(listings, this.hourlySeed)
        : listings;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [
      customPrimaryFilters,
      customSecondaryFilters,
    ] = groupListingFieldConfigs(listingFieldsConfig, activeListingTypes);
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Format filter values for display (convert codes to readable labels)
    const formattedFilters = {};
    VALID_FILTER_PARAMS.forEach(param => {
      if (searchParamsInURL[param] !== undefined) {
        formattedFilters[param] = searchParamsInURL[param];
      }
    });

    // Handle condition values properly
    if (formattedFilters.pub_condition) {
      try {
        // If it's a comma-separated string of multiple conditions
        if (
          typeof formattedFilters.pub_condition === 'string' &&
          formattedFilters.pub_condition.includes(',')
        ) {
          formattedFilters.pub_condition = formattedFilters.pub_condition
            .split(',')
            .map(val => {
              const trimmedVal = val.trim();
              // Use ConditionToLabel as a function
              return ConditionToLabel(trimmedVal) || trimmedVal;
            })
            .join(', ');
        }
        // If it's a single condition string
        else {
          // Make sure to call ConditionToLabel as a function
          const labelResult = ConditionToLabel(
            String(formattedFilters.pub_condition)
          );
          formattedFilters.pub_condition = labelResult;
        }
      } catch (error) {
        console.error('Error handling condition values:', error);
        // If there's an error, keep the original value
      }
    }

    // Capitalize first letter of all filter values
    Object.keys(formattedFilters).forEach(key => {
      if (typeof formattedFilters[key] === 'string') {
        formattedFilters[key] =
          formattedFilters[key].charAt(0).toUpperCase() +
          formattedFilters[key].slice(1);
      } else if (Array.isArray(formattedFilters[key])) {
        formattedFilters[key] = formattedFilters[key].map(value => {
          if (typeof value === 'string') {
            return value.charAt(0).toUpperCase() + value.slice(1);
          }
          return value;
        });
      }
    });

    // Extract only the values into an array for comma-separated list
    const filterValuesArray = [];

    // Loop through formatted filters and collect only values
    Object.values(formattedFilters).forEach(value => {
      if (value) {
        if (Array.isArray(value)) {
          // If it's an array, add each individual value
          value.forEach(item => {
            if (item && typeof item === 'string' && item.trim() !== '') {
              filterValuesArray.push(item);
            }
          });
        } else if (typeof value === 'string' && value.trim() !== '') {
          // Add non-empty string values
          filterValuesArray.push(value);
        }
      }
    });

    // Create a comma-separated string of all filter values
    const formattedFiltersList = filterValuesArray.join(', ');

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null &&
        searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    // "Save search" is offered for keyword searches with a non-empty phrase.
    const currentKeyword = (parse(location.search).keywords || '').trim();
    const savedSearches =
      currentUser?.attributes?.profile?.privateData?.savedSearches || [];
    const isCurrentSearchSaved =
      Array.isArray(savedSearches) &&
      savedSearches.some(
        s =>
          (s?.searchPhrase || '').trim().toLowerCase() ===
          currentKeyword.toLowerCase()
      );
    const saveSearchButtonMaybe =
      isKeywordSearch && currentKeyword ? (
        <SaveSearchButton
          isSaved={isCurrentSearchSaved}
          inProgress={updateInProgress}
          onClick={this.handleToggleSavedSearch}
        />
      ) : null;

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // Handle favorite toggle
    const handleToggleFavorites = listingId => isFavorite => {
      if (!currentUser) return;

      const { favorites = [] } =
        currentUser.attributes.profile.privateData || {};
      let updatedFavorites;

      if (isFavorite) {
        // Remove from favorites
        updatedFavorites = favorites.filter(f => f !== listingId);
      } else {
        // Add to favorites
        updatedFavorites = [...favorites, listingId];
      }

      const payload = {
        privateData: {
          ...currentUser.attributes.profile.privateData,
          favorites: updatedFavorites,
        },
      };

      onUpdateProfile(payload);
    };

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarSpacer />
        <div className={css.layoutWrapperContainer}>
          <div className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                saveSearchComponent={saveSearchButtonMaybe}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                formattedFilters={formattedFiltersList}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope ||
                    'built-in'}.${filterConfig.key}`;

                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersMobile"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(
                        this.props,
                        this.state.currentQueryParams
                      )}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <div className={css.listingsForGridVariant}>
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={processedListings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  onToggleFavorites={handleToggleFavorites}
                  currentUser={currentUser}
                  searchInProgress={searchInProgress}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  currentUser: propTypes.currentUser,
  onUpdateProfile: func.isRequired,
  updateInProgress: bool,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace =
    config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(
    searchListingsError
  );
  if (
    (isPrivateMarketplace && isUnauthorizedUser) ||
    hasUserPendingApprovalError
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);
  const { updateInProgress } = state.ProfileSettingsPage;

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    updateInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateProfile: payload => dispatch(updateProfile(payload)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(
  EnhancedSearchPage
);

export default SearchPage;
